import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import Paragraph from '@/components/Paragraph';
import { PageInfoType } from '@/components/Page';
import Badge, { BadgeType } from '@/components/Badge';
import Link, { LinkStyle, createInternalLink } from '@/components/Link';
import Image, { ImageType } from '@/components/Image';

const ImageAndBadgeWrapper = styled.div`
  position: relative;
  display: flex;
`;

const BadgeWrapper = styled.div`
  position: absolute;
  z-index: 10;
  top: -7px;
  left: 50%;
  transform: translateX(-50%);
`;

const TileInnerWrapper = styled.div`
  display: flex;
  box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.12),
    0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  border-radius: 12px;

  &:hover {
    box-shadow: rgb(167 189 204) 0px 0px 0px 1px,
      rgb(0 0 0 / 8%) 0px 15px 20px 0px, rgb(0 0 0 / 8%) 0px 19px 40px 0px;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: rgba(167, 189, 204, 0.5) 0px 0px 0px 0.4rem;
  }
`;

const TileImageWrapper = styled.div`
  flex-shrink: 0;
  width: 140px;
  border-radius: 10px 0 0 10px;
  overflow: hidden;
`;

const TileImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TileContent = styled.div`
  flex-grow: 1;
  padding: 2.4rem;
  background-color: #fff;
  border-radius: 0 12px 12px 0;

  p:first-child {
    line-height: 2.4rem;
  }
  p:last-child {
    line-height: 2rem;
  }
`;

export type Props = TileType;

export type TileType = {
  badge?: BadgeType;
  title: string;
  text: {
    text: string;
  };
  image: ImageType;
  link: PageInfoType;
};

const Tile: React.FC<Props> = ({ link, title, badge, image, text }) => (
  <li>
    <Link {...createInternalLink(link, LinkStyle.CONTENT)}>
      <TileInnerWrapper>
        <ImageAndBadgeWrapper>
          {badge && (
            <BadgeWrapper>
              <Badge {...badge} />
            </BadgeWrapper>
          )}
          <TileImageWrapper>
            <TileImage {...image} />
          </TileImageWrapper>
        </ImageAndBadgeWrapper>
        <TileContent>
          <Paragraph size='md' weight='bold' margin='0 0 .8rem'>
            {title}
          </Paragraph>
          <Paragraph size='sm' color='GreyBlue'>
            {text.text}
          </Paragraph>
        </TileContent>
      </TileInnerWrapper>
    </Link>
  </li>
);
export default Tile;

export const smallCardFragmentQuery = graphql`
  fragment TileFragment on ContentfulTile {
    title
    badge {
      ...BadgeFragment
    }
    text {
      text
    }
    link {
      ...PagePathFragment
    }
    image {
      gatsbyImageData(width: 140, height: 140)
      ...ImageFragment
    }
  }
`;
